import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LineChart, Line,
} from "recharts";
import {
  TrendingUp, TrendingDown, UserPlus, RefreshCw, Users,
  ArrowUpRight, ArrowDownRight, Minus, Database, BarChart3,
} from "lucide-react";
import { cn } from "@/lib/utils";

const API = "/api";
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtMonth = (k) => { if (!k) return ""; const [y, m] = k.split("-"); return `${MONTH_NAMES[parseInt(m) - 1]} ${y}`; };

function Growth({ value }) {
  if (!value) return <span className="flex items-center text-xs text-slate-400"><Minus className="w-3 h-3 mr-0.5" /> 0%</span>;
  if (value > 0) return <span className="flex items-center text-xs text-green-600 font-semibold"><ArrowUpRight className="w-3.5 h-3.5 mr-0.5" /> +{value}%</span>;
  return <span className="flex items-center text-xs text-red-600 font-semibold"><ArrowDownRight className="w-3.5 h-3.5 mr-0.5" /> {value}%</span>;
}

function StatCard({ title, value, growth, icon: Icon, color, sub }) {
  const bgMap = { "text-indigo-700": "bg-indigo-100", "text-amber-700": "bg-amber-100", "text-emerald-700": "bg-emerald-100", "text-slate-700": "bg-slate-100" };
  return (
    <Card data-testid={`stat-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <CardContent className="pt-5 pb-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{title}</p>
            <p className={cn("text-3xl font-black mt-1", color)}>{value}</p>
            {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
          </div>
          <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", bgMap[color] || "bg-slate-100")}>
            <Icon className={cn("w-5 h-5", color)} />
          </div>
        </div>
        {growth !== null && growth !== undefined && (
          <div className="mt-2 flex items-center gap-1">
            <Growth value={growth} />
            <span className="text-[10px] text-slate-400">vs last month</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function SalesAnalytics() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [chartView, setChartView] = useState("bar");
  const [txFilter, setTxFilter] = useState("all");

  const fetchData = useCallback(async () => {
    try {
      const [aRes, tRes] = await Promise.all([
        axios.get(`${API}/analytics/sales?months=12`, { headers: getAuthHeader() }),
        axios.get(`${API}/analytics/sales/transactions`, { headers: getAuthHeader() }),
      ]);
      setData(aRes.data);
      setTransactions(tRes.data);
    } catch {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/analytics/sales/sync`, {}, { headers: getAuthHeader() });
      toast.success(`Synced: ${res.data.enrollments} enrollments, ${res.data.renewals} renewals`);
      fetchData();
    } catch {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20 text-slate-400">Loading...</div>;

  const s = data?.summary || {};
  const monthly = (data?.monthly_data || []).map((m) => ({ ...m, label: fmtMonth(m.month) }));
  const filteredTx = txFilter === "all" ? transactions : transactions.filter((t) => t.type === txFilter);

  return (
    <div className="space-y-6" data-testid="sales-analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Student Growth</h1>
          <p className="text-slate-500">Track enrollments, renewals & student activity over time</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing} data-testid="sync-btn">
          <Database className="w-4 h-4 mr-2" /> {syncing ? "Syncing..." : "Sync Historical Data"}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="New Enrollments" value={s.new_enrollments || 0} growth={s.enrollment_growth} icon={UserPlus} color="text-indigo-700" sub={`All-time: ${s.all_time_enrollments || 0}`} />
        <StatCard title="Renewals" value={s.renewals || 0} growth={s.renewal_growth} icon={RefreshCw} color="text-amber-700" sub={`All-time: ${s.all_time_renewals || 0}`} />
        <StatCard title="Active Students" value={s.active_students || 0} growth={null} icon={Users} color="text-emerald-700" sub="Currently active" />
        <StatCard title="Total Enrolled" value={s.all_time_enrollments || 0} growth={null} icon={BarChart3} color="text-slate-700" sub={`${s.new_enrollments || 0} this month`} />
      </div>

      {/* Chart */}
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base">Monthly Trends</CardTitle>
          <Select value={chartView} onValueChange={setChartView}>
            <SelectTrigger className="w-36" data-testid="chart-view-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="bar">Bar Chart</SelectItem>
              <SelectItem value="line">Line Chart</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {monthly.length === 0 ? (
            <div className="text-center py-16 text-slate-400">No data yet. Click "Sync Historical Data" to populate.</div>
          ) : (
            <ResponsiveContainer width="100%" height={340}>
              {chartView === "line" ? (
                <LineChart data={monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="enrollments" stroke="#6366f1" strokeWidth={2.5} dot={{ r: 5, fill: "#6366f1" }} name="New Enrollments" />
                  <Line type="monotone" dataKey="renewals" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 5, fill: "#f59e0b" }} name="Renewals" />
                  <Line type="monotone" dataKey="active_students" stroke="#10b981" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 4, fill: "#10b981" }} name="Cumulative Students" />
                </LineChart>
              ) : (
                <BarChart data={monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="enrollments" fill="#6366f1" radius={[4, 4, 0, 0]} name="New Enrollments" />
                  <Bar dataKey="renewals" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Renewals" />
                </BarChart>
              )}
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Growth Table */}
      {monthly.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Month-over-Month</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-bold">Month</TableHead>
                  <TableHead className="font-bold text-center">New Enrollments</TableHead>
                  <TableHead className="font-bold text-center">Renewals</TableHead>
                  <TableHead className="font-bold text-center">Total Activity</TableHead>
                  <TableHead className="font-bold text-center">Cumulative Students</TableHead>
                  <TableHead className="font-bold text-center">Enrollment Trend</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthly.map((m) => (
                  <TableRow key={m.month}>
                    <TableCell className="font-medium">{m.label}</TableCell>
                    <TableCell className="text-center"><Badge className="bg-indigo-100 text-indigo-700">{m.enrollments}</Badge></TableCell>
                    <TableCell className="text-center"><Badge className="bg-amber-100 text-amber-700">{m.renewals}</Badge></TableCell>
                    <TableCell className="text-center text-sm font-medium">{m.total_activity}</TableCell>
                    <TableCell className="text-center text-sm font-medium text-emerald-700">{m.active_students}</TableCell>
                    <TableCell className="text-center"><Growth value={m.enrollment_growth} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Recent Activity */}
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base">Recent Activity</CardTitle>
          <Select value={txFilter} onValueChange={setTxFilter}>
            <SelectTrigger className="w-36" data-testid="tx-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="enrollment">Enrollments</SelectItem>
              <SelectItem value="renewal">Renewals</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-bold">Student</TableHead>
                <TableHead className="font-bold">Type</TableHead>
                <TableHead className="font-bold text-center">Classes</TableHead>
                <TableHead className="font-bold">Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTx.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-400">No records found</TableCell></TableRow>
              ) : (
                filteredTx.slice(0, 50).map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-medium text-slate-900">{t.student_name}</TableCell>
                    <TableCell>
                      <Badge className={cn("text-xs", t.type === "enrollment" ? "bg-indigo-100 text-indigo-700" : "bg-amber-100 text-amber-700")}>
                        {t.type === "enrollment" ? "New Enrollment" : "Renewal"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">{t.classes_count}</TableCell>
                    <TableCell className="text-sm text-slate-600">{t.date}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
